import { useState, useEffect, useCallback, useRef } from 'react';
import productCategoryService from '../services/productCategoryService';
import taxesService from '../services/taxesService';
import { useMessageAlert } from '../components/common/MessageAlert';
import i18n from '../i18n';

/**
 * Custom hook for managing product categories data and operations
 * @param {Object} initialOptions - Initial configuration options
 * @returns {Object} Product category state and methods
 */
export const useProductCategory = (initialOptions = {}) => {
  const {
    autoFetch = true,
    pageSize = 20,
    initialFilters = {}
  } = initialOptions;

  // Message alert for success notifications
  const { showCreate, showUpdate, showDelete } = useMessageAlert();

  // State management
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    tax_id: '',
    ...initialFilters
  });

  // Sort state
  const [sortConfig, setSortConfig] = useState({
    key: 'name',
    direction: 'asc'
  });

  // Refs for state tracking
  const currentPageRef = useRef(currentPage);
  const filtersRef = useRef(filters);
  const sortConfigRef = useRef(sortConfig);
  const pageSizeRef = useRef(pageSize);
  const lastFetchSignatureRef = useRef(null);
  const inFlightSignatureRef = useRef(null);
  
  // Deduplication helpers
  const lastSignatureRef = useRef('');
  const lastFetchTimeRef = useRef(0);
  const inFlightSetRef = useRef(new Set());
  
  // Client-side cache for categories to support local search without repeated API calls
  const cacheRef = useRef([]);

  // Form and modal state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Update refs when state changes
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    sortConfigRef.current = sortConfig;
  }, [sortConfig]);

  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  const shouldDedupe = useCallback((signature, force) => {
    if (force) return false;
    const now = Date.now();
    const elapsed = now - (lastFetchTimeRef.current || 0);
    return signature === lastSignatureRef.current && elapsed < 200;
  }, []);

  /**
   * Fetch categories with current filters and pagination
   */
  const fetchCategories = useCallback(async (options = {}) => {
    const page = options.page ?? currentPageRef.current;

    const params = {
      page,
      limit: options.limit ?? pageSizeRef.current,
      search: filtersRef.current.search,
      tax_id: filtersRef.current.tax_id,
      sort_by: sortConfigRef.current.key,
      sort_order: sortConfigRef.current.direction,
      ...options.params
    };

    const signature = JSON.stringify(params);

    // Deduplicate: skip if identical request was recently made or currently in-flight
    if (!options.force) {
      if (inFlightSetRef.current.has(signature)) {
        return;
      }
      if (shouldDedupe(signature, options.force)) {
        return;
      }
    }

    // mark as in-flight
    inFlightSetRef.current.add(signature);
    setLoading(true);
    setError(null);

    try {
      const result = await productCategoryService.getProductCategories(params);

      if (result.success) {
        const list = Array.isArray(result.data) ? result.data : [];
        // update client-side cache when fetching from server
        cacheRef.current = list;
        setCategories(list);

        const totalRecords =
          result.total ?? result.pagination?.total ?? list.length;
        setTotalCount(totalRecords);

        const computedTotalPages =
          result.pagination?.total_pages ??
          Math.max(1, Math.ceil(totalRecords / (params.limit || pageSizeRef.current || 1)));
        setTotalPages(computedTotalPages);

        if (options.page && options.page !== currentPageRef.current) {
          setCurrentPage(options.page);
        }
      } else {
        setCategories([]);
        setError(result.error || i18n.t('productCategories.errors.fetchFailed'));
      }
    } catch (err) {
      setCategories([]);
      setError(i18n.t('productCategories.errors.fetchFailed'));
    } finally {
      // persist last signature/time for dedupe checks
      lastSignatureRef.current = signature;
      lastFetchTimeRef.current = Date.now();
      // clear in-flight marker
      inFlightSetRef.current.delete(signature);
      lastFetchSignatureRef.current = { signature, timestamp: Date.now() };
      inFlightSignatureRef.current = null;
      setLoading(false);
    }
  }, [shouldDedupe]);


  /**
   * Get category by ID
   */
  const getCategoryById = useCallback(async (categoryId, options = {}) => {
    const { withLoading = false } = options;

    if (withLoading) {
      setLoading(true);
    }
    setError(null);

    try {
      const result = await productCategoryService.getProductCategoryById(categoryId);
      
      if (result.success) {
        return result.data;
      } else {
        setError(result.error);
        return null;
      }
    } catch (err) {
      setError(i18n.t('productCategories.errors.fetchFailed'));
      return null;
    } finally {
      if (withLoading) {
        setLoading(false);
      }
    }
  }, []);

  /**
   * Handle page change
   */
  const handlePageChange = useCallback((page) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchCategories({ page, force: true });
    }
  }, [currentPage, totalPages, fetchCategories]);

  /**
   * Handle filter change
   */
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(prevFilters => {
      const merged = { ...prevFilters, ...newFilters };
      setCurrentPage(1);
      // Always server-fetch so search goes to the API with the search query param
      setTimeout(() => {
        fetchCategories({ page: 1, force: true });
      }, 300);
      return merged;
    });
  }, [fetchCategories]);

  /**
   * Handle sort change
   */
  const handleSortChange = useCallback((key, direction) => {
    setSortConfig({ key, direction });
    fetchCategories({ force: true });
  }, [fetchCategories]);

  /**
   * Handle selection change
   */
  const handleSelectionChange = useCallback((selected) => {
    setSelectedCategories(selected);
  }, []);

  // Legacy search handler for backward compatibility
  const handleSearch = useCallback((term) => {
    handleFilterChange({ search: term });
  }, [handleFilterChange]);

  // Legacy sort handler for backward compatibility  
  const handleSort = useCallback((column) => {
    const currentSort = sortConfig.key === column ? sortConfig.direction : 'asc';
    const newDirection = currentSort === 'asc' ? 'desc' : 'asc';
    handleSortChange(column, newDirection);
  }, [sortConfig, handleSortChange]);

  /**
   * Selection methods
   */
  const selectCategory = useCallback((categoryId, isSelected) => {
    if (isSelected) {
      setSelectedCategories(prev => [...prev, categoryId]);
    } else {
      setSelectedCategories(prev => prev.filter(id => id !== categoryId));
    }
  }, []);

  const selectAllCategories = useCallback((shouldSelectAll = true) => {
    if (shouldSelectAll) {
      setSelectedCategories(categories.map(category => category.id));
    } else {
      setSelectedCategories([]);
    }
  }, [categories]);

  const clearSelection = useCallback(() => {
    setSelectedCategories([]);
  }, []);

  const isCategorySelected = useCallback((categoryId) => {
    return selectedCategories.includes(categoryId);
  }, [selectedCategories]);

  const isAllSelected = useCallback(() => {
    return categories.length > 0 && selectedCategories.length === categories.length;
  }, [categories, selectedCategories]);

  // Legacy selection handlers for backward compatibility
  const handleSelectCategory = useCallback((categoryId) => {
    const isSelected = selectedCategories.includes(categoryId);
    selectCategory(categoryId, !isSelected);
  }, [selectedCategories, selectCategory]);

  const handleSelectAll = useCallback(() => {
    const allSelected = isAllSelected();
    selectAllCategories(!allSelected);
  }, [isAllSelected, selectAllCategories]);

  const clearSelections = useCallback(() => {
    clearSelection();
  }, [clearSelection]);

  /**
   * Form handlers
   */
  const openAddForm = useCallback(() => {
    setEditingCategory(null);
    setIsFormOpen(true);
  }, []);

  const openEditForm = useCallback((category) => {
    setEditingCategory(category);
    setIsFormOpen(true);
  }, []);

  const closeForm = useCallback(() => {
    setIsFormOpen(false);
    setEditingCategory(null);
  }, []);

  /**
   * Details modal handlers
   */
  const openDetailsModal = useCallback((category) => {
    setSelectedCategory(category);
    setIsDetailsModalOpen(true);
  }, []);

  const closeDetailsModal = useCallback(() => {
    setIsDetailsModalOpen(false);
    setSelectedCategory(null);
  }, []);

  /**
   * Import modal handlers
   */
  const openImportModal = useCallback(() => {
    setIsImportModalOpen(true);
  }, []);

  const closeImportModal = useCallback(() => {
    setIsImportModalOpen(false);
  }, []);

  /**
   * Create new category
   */
  const createCategory = useCallback(async (categoryData) => {
    setError(null);

    try {
      const result = await productCategoryService.createProductCategory(categoryData);
      
      if (result.success) {
        // Update local state instead of refetching
        let newCategory = result.data;

        // If created category has a tax_id, try to fetch the full tax details so GST rate
        // is available immediately in the UI (mirrors updateCategory behavior)
        if (newCategory && newCategory.tax_id) {
          try {
            const taxResult = await taxesService.getTaxById(newCategory.tax_id);
            if (taxResult && taxResult.success && taxResult.data) {
              newCategory = {
                ...newCategory,
                tax: taxResult.data,
                tax_gst_rate: taxResult.data.gst || taxResult.data.gst_rate || taxResult.data.percentage,
                gst_rate: taxResult.data.gst || taxResult.data.gst_rate || taxResult.data.percentage,
              };
            }
          } catch (taxErr) {
            // Non-fatal: continue without enriched tax if fetch fails
            console.warn('Failed to fetch tax details for new category:', taxErr);
          }
        }

        setCategories(prevCategories => {
          const updatedCategories = [newCategory, ...prevCategories];
          // Update cache as well
          cacheRef.current = updatedCategories;
          return updatedCategories;
        });

        // Update total count
        setTotalCount(prevCount => prevCount + 1);
        
        // Recalculate total pages
        setTotalPages(prevPages => Math.max(1, Math.ceil((totalCount + 1) / pageSizeRef.current)));

        // Prefer server message when provided, otherwise use localized template
        if (result.message) {
          showCreate(result.message);
        } else {
          const categoryName = newCategory?.name || i18n.t('productCategories.table.copy.unnamedCategory');
          showCreate(i18n.t('productCategories.messages.createSuccess', { name: categoryName }));
        }

        return { success: true, data: newCategory };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err?.message || i18n.t('productCategories.errors.createFailed');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showCreate, totalCount]);

  /**
   * Update category
   */
  const updateCategory = useCallback(async (categoryId, categoryData) => {
    setError(null);

    try {
      const result = await productCategoryService.updateProductCategory(categoryId, categoryData);
      
      if (result.success) {
        // Update local state instead of refetching
        let updatedCategory = result.data;
        
        // If tax_id was updated, fetch the full tax details to show GST rate correctly
        if (categoryData.tax_id && updatedCategory.tax_id) {
          try {
            const taxResult = await taxesService.getTaxById(updatedCategory.tax_id);
            
            if (taxResult.success && taxResult.data) {
              // Merge the full tax object into the category
              updatedCategory = {
                ...updatedCategory,
                tax: taxResult.data,
                tax_gst_rate: taxResult.data.gst || taxResult.data.gst_rate || taxResult.data.percentage,
                gst_rate: taxResult.data.gst || taxResult.data.gst_rate || taxResult.data.percentage
              };
            }
          } catch (taxError) {
            console.warn('Failed to fetch tax details for category:', taxError);
            // Continue with update even if tax fetch fails
          }
        }
        
        setCategories(prevCategories => {
          const updatedCategories = prevCategories.map(category => 
            category.id === categoryId ? { ...category, ...updatedCategory } : category
          );
          // Update cache as well
          cacheRef.current = updatedCategories;
          return updatedCategories;
        });

        // Update selected category if it's the one being edited
        if (selectedCategory && selectedCategory.id === categoryId) {
          setSelectedCategory(prevSelected => ({ ...prevSelected, ...updatedCategory }));
        }

        // Prefer server message when available
        if (result.message) {
          showUpdate(result.message);
        } else {
          const categoryName = result.data?.name || i18n.t('productCategories.table.copy.unnamedCategory');
          showUpdate(i18n.t('productCategories.messages.updateSuccess', { name: categoryName }));
        }

        return { success: true, data: result.data };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err?.message || i18n.t('productCategories.errors.updateFailed');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showUpdate, selectedCategory]);

  /**
   * Delete category
   */
  const deleteCategory = useCallback(async (categoryId) => {
    setError(null);

    try {
      // Get category details before deletion for the message
      const categoryToDelete = categories.find(category => category.id === categoryId);

      const result = await productCategoryService.deleteProductCategory(categoryId);

      // Prefer server returned name, then local cache, then fallback to id
      const categoryName = result?.data?.name || categoryToDelete?.name || String(categoryId);

      if (result.success) {
        // Update local state instead of refetching
        setCategories(prevCategories => {
          const updatedCategories = prevCategories.filter(category => category.id !== categoryId);
          // Update cache as well
          cacheRef.current = updatedCategories;
          return updatedCategories;
        });

        // Update total count
        setTotalCount(prevCount => Math.max(0, prevCount - 1));
        
        // Recalculate total pages
        setTotalPages(prevPages => Math.max(1, Math.ceil(Math.max(0, totalCount - 1) / pageSizeRef.current)));

        // Remove from selection if selected
        setSelectedCategories(prevSelected => prevSelected.filter(id => id !== categoryId));

        // Close details modal if the deleted category is currently shown
        if (selectedCategory && selectedCategory.id === categoryId) {
          setSelectedCategory(null);
          setIsDetailsModalOpen(false);
        }

        // Prefer server message when available
        if (result.message) {
          showDelete(result.message);
        } else {
          showDelete(i18n.t('productCategories.messages.deleteSuccess', { name: categoryName }));
        }

        return { success: true };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err?.message || i18n.t('productCategories.errors.deleteFailed');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [categories, showDelete, totalCount, selectedCategory]);

  /**
   * Bulk delete categories
   */
  const bulkDeleteCategories = useCallback(async (categoryIds) => {
    if (!categoryIds || categoryIds.length === 0) {
      return { success: false, error: i18n.t('productCategories.messages.noSelection') };
    }

    setError(null);

    try {
      // Capture category names from local cache where possible (fallback to id)
      const categoryNames = categoryIds.map(id => {
        const c = categories.find(cat => cat.id === id);
        return c?.name || String(id);
      });

      const result = await productCategoryService.bulkDeleteProductCategories(categoryIds);

      if (result.success) {
        // Update local state instead of refetching
        setCategories(prevCategories => {
          const updatedCategories = prevCategories.filter(category => !categoryIds.includes(category.id));
          // Update cache as well
          cacheRef.current = updatedCategories;
          return updatedCategories;
        });

        // Update total count
        setTotalCount(prevCount => Math.max(0, prevCount - categoryIds.length));
        
        // Recalculate total pages
        setTotalPages(prevPages => Math.max(1, Math.ceil(Math.max(0, totalCount - categoryIds.length) / pageSizeRef.current)));

        // Remove deleted categories from selection
        setSelectedCategories(prevSelected => prevSelected.filter(id => !categoryIds.includes(id)));

        // Close details modal if the deleted category is currently shown
        if (selectedCategory && categoryIds.includes(selectedCategory.id)) {
          setSelectedCategory(null);
          setIsDetailsModalOpen(false);
        }

        // Prefer server message when provided
        if (result.message) {
          showDelete(result.message);
        } else {
          if (categoryIds.length === 1) {
            const name = categoryNames[0] || String(categoryIds[0]);
            showDelete(i18n.t('productCategories.messages.deleteSuccess', { name: name }));
          } else {
            showDelete(i18n.t('productCategories.messages.bulkDeleteSuccess', { count: categoryIds.length }));
          }
        }

        return { success: true };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err?.message || i18n.t('productCategories.errors.bulkDeleteFailed');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [categories, showDelete, totalCount, selectedCategory]);

  /**
   * Import categories
   */
  const importCategories = useCallback(async (file) => {
    setLoading(true);
    setError(null);

    try {
      const result = await productCategoryService.importProductCategories(file);

      if (result.success) {
        // For import, we refresh from the server since we don't know the exact imported data
        await fetchCategories({ force: true });

        // Partial failures: the backend creates the good rows and returns the rejected
        // ones in data.errors / data.total_errors. Previously those were ignored and the
        // import reported a clean success, so failed rows vanished silently.
        const data = result.data || {};
        const errs = Array.isArray(data.errors) ? data.errors : [];
        if (errs.length > 0 || (typeof data.total_errors === 'number' && data.total_errors > 0)) {
          const list = errs.length ? errs : [i18n.t('productCategories.errors.importFailed')];
          setError(list);
          return { success: false, error: list, data };
        }

        // Prefer server message when available
        if (result.message) showCreate(result.message);
        else showCreate(i18n.t('productCategories.messages.importSuccess'));

        return { success: true };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err?.message || i18n.t('productCategories.errors.importFailed');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [fetchCategories, showCreate]);

  /**
   * Export categories
   */
  const exportCategories = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        search: filters.search?.trim() || undefined,
        tax_id: filters.tax_id || undefined,
        sort_by: sortConfig.key,
        sort_order: sortConfig.direction,
      };

      const result = await productCategoryService.exportProductCategories(params);

      if (result.success) {
        // Create download link
        const url = window.URL.createObjectURL(new Blob([result.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', result.filename || 'product_categories.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

  if (result.message) showCreate(result.message);
  else showCreate(i18n.t('productCategories.messages.exportSuccess'));
        return { success: true };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err?.message || i18n.t('productCategories.errors.exportFailed');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [filters, sortConfig, showCreate]);

  /**
   * Download sample template
   */
  const downloadSampleTemplate = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await productCategoryService.downloadSampleTemplate();

      if (result.success) {
        // Create download link
        const url = window.URL.createObjectURL(new Blob([result.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', result.filename || 'product_categories_sample.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

  if (result.message) showCreate(result.message);
  else showCreate(i18n.t('productCategories.messages.templateDownloadSuccess'));
        return { success: true };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err?.message || i18n.t('productCategories.errors.templateDownloadFailed');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [showCreate]);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setFilters({
      search: '',
      tax_id: ''
    });
    setCurrentPage(1);
    fetchCategories({ page: 1, force: true });
  }, [fetchCategories]);

  /**
   * Refresh data
   */
  const refresh = useCallback(() => {
    fetchCategories({ force: true });
  }, [fetchCategories]);

  // Legacy refresh method for backward compatibility
  const refreshData = useCallback(() => {
    clearSelection();
    refresh();
  }, [clearSelection, refresh]);

  // Computed values
  const hasCategories = categories.length > 0;
  const hasSelection = selectedCategories.length > 0;
  const selectionCount = selectedCategories.length;

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      fetchCategories();
    }
  }, [autoFetch, fetchCategories]);


  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // Data
    categories,
    loading,
    error,
    clearError,
    selectedCategories,
    
    // Pagination
    currentPage,
    totalPages,
    totalCount,
    
    // Filters and sorting
    filters,
    sortConfig,
    
    // Methods
    fetchCategories,
    getCategoryById,
    createCategory,
    updateCategory,
    deleteCategory,
    bulkDeleteCategories,
    handlePageChange,
    handleFilterChange,
    handleSortChange,
    handleSelectionChange,
    selectCategory,
    selectAllCategories,
    clearSelection,
    isCategorySelected,
    isAllSelected,
    clearFilters,
    refresh,
    
    // Computed values
    hasCategories,
    hasSelection,
    selectionCount,
    
    // State setters (for direct manipulation if needed)
    setCategories,
    setLoading,
    setError,
    setSelectedCategories,
    setFilters,
    setSortConfig,

    // Legacy compatibility (for existing components)
    totalRecords: totalCount,
    pageSize: pageSizeRef.current,
    searchTerm: filters.search,
    sortBy: sortConfig.key,
    sortOrder: sortConfig.direction,
    selectAll: isAllSelected(),
    hasSelectedCategories: hasSelection,
    selectedCount: selectionCount,
    
    // Modal state (unchanged for backward compatibility)
    isFormOpen,
    isImportModalOpen,
    isDetailsModalOpen,
    editingCategory,
    selectedCategory,
    
    // Legacy methods (for backward compatibility)
    loadCategories: fetchCategories,
    refreshData,
    handleSearch,
    handleSort,
    handleSelectCategory,
    handleSelectAll,
    clearSelections,
    openAddForm,
    openEditForm,
    closeForm,
    openDetailsModal,
    closeDetailsModal,
    openImportModal,
    closeImportModal,
    importCategories,
    exportCategories,
    downloadSampleTemplate,
  };
};

export default useProductCategory;